import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CAPI_PREVIEW_IMAGE_REL, URL_GREYED_OUT_CAR } from '@/constants/images';
import { useTribalContext } from '@/context/tribal';
import usePdb from '@/hooks/usePdb';
import {
  CapiImage,
  FixedTopContainer,
  PageContainer,
  VehicleCardProps,
  VehicleCardsContainer,
} from '@/modules/components';
import {
  getDisabledModelMessage,
  getModelTrim,
  isNotBuildable,
  isStockCar,
} from '@/modules/utils/storeUtils';
import useGlobalStore, {
  type GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import { formatCurrency } from '@/modules/utils/currency';
import {
  StyledVehicleCards,
  StyledVehicleCardsNav,
} from './VehicleCards.styles';
import { performVWBasicButtonClick } from '@/analytics/events';
import { SectionNames } from '@/analytics/sectionNames';
import useAppNavigate from '@/hooks/useAppNavigate';
import useAgentService from '@/hooks/useAgentService';

interface VehicleCardsProps {
  configurations: GlobalStoreStateConfiguration[];
  pdbId?: string;
  onSlideChange?: (index: number) => void;
  resetSlides?: boolean;
}

const VehicleCards = ({
  configurations,
  pdbId,
  onSlideChange,
  resetSlides,
}: VehicleCardsProps) => {
  const { contentManager, analyticsManager, analyticsBuilder } =
    useTribalContext();
  const navigate = useAppNavigate();
  const { retailers } = useGlobalStore((state) => state.app);
  const ref = useRef<HTMLDivElement>(null);

  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [reveal, setReveal] = useState(false);

  const { pdfUrl } = usePdb({ pdbId });
  const { getConfiguratorUrl } = useAgentService();

  const handleSlideChange = useCallback((index: number) => {
    setActiveSlideIndex(index);

    onSlideChange?.(index);
  }, []);

  useEffect(() => {
    const footers = document.getElementsByTagName('footer');
    let footer: HTMLElement;

    const observer = new IntersectionObserver((entries) => {
      if (entries.every((entry) => !entry.isIntersecting)) setReveal(true);
      else setReveal(false);
    });

    if (ref.current) observer.observe(ref.current);

    if (!!footers.length) {
      footer = footers[footers.length - 1];
      observer.observe(footer);
    }

    return () => observer.disconnect();
  }, []);

  const vehiclePriceDisclaimer = (
    <div>
      <p>{contentManager.get('prices-disclaimer-text')}</p>
    </div>
  );

  const vehicles = useMemo(() => {
    return configurations
      .map((configuration, index) => {
        const { pdbData, fullUrl, modelTitle, trimTitle, derivativeTitle } = configuration;
        if (isNotBuildable(pdbData)) {
          const disabledMessage = getDisabledModelMessage(
            contentManager,
            modelTitle,
            derivativeTitle || trimTitle
          );

          return {
            title: getModelTrim(modelTitle, trimTitle),
            disabledMessage,
            image: {
              url: URL_GREYED_OUT_CAR,
              alt: disabledMessage,
            },
            disabled: true,
            pdfLink: {
              url: pdfUrl,
              text: contentManager.get('ctas-download-this-pdf'),
            },
            configuratorLink: {
              url: getConfiguratorUrl(configuration),
              text: contentManager.get('ctas-open-configurator'),
            },
          };
        } else if (pdbData?.derivative && pdbData?.pricing && pdbData?.capi) {
          const {
            derivative: { modelTitle, title },
            pricing: {
              retail,
              extras,
              bespokeExtras,
              tradeIn,
              grants,
              outstandingTotal,
              allowance,
              discount
            },
            capi: { images },
          } = pdbData;

          const previewImage = images.find(
            ({ rel }: CapiImage) => rel === CAPI_PREVIEW_IMAGE_REL
          );

          if (previewImage) {
            const { title: alt, uri: url } = previewImage;

            return {
              disclaimerText: vehiclePriceDisclaimer,
              title: getModelTrim(modelTitle, trimTitle),
              carSummary: title,
              totalPriceLabel: contentManager.get('total-price-label'),
              totalPrice: formatCurrency(outstandingTotal),
              p11dPrice: pdbData.fleetMode ? formatCurrency(pdbData.pricing.p11dPrice) : null,
              image: {
                url,
                alt,
              },
              stockDescription: isStockCar(configurations[index])
                ? contentManager.get('stock-badge-info')
                : '',
              priceBreakdown: {
                prices: [
                  {
                    label: contentManager.get(
                      'price-breakdown-section-base-price-label'
                    ),
                    price: retail,
                  },
                  {
                    label: contentManager.get(
                      'price-breakdown-section-extras-label'
                    ),
                    price: extras,
                  },
                  {
                    label: contentManager.get(
                      'price-breakdown-section-bespoke-options-label'
                    ),
                    price: bespokeExtras,
                  },
                  {
                    label: contentManager.get(
                      'price-breakdown-section-trade-in-label'
                    ),
                    info: contentManager.get(
                      'price-breakdown-section-set-by-retailer-text'
                    ),
                    price: tradeIn,
                  },
                  {
                    label: contentManager.get(
                      'price-breakdown-section-allowance-label'
                    ),
                    info: contentManager.get(
                      'price-breakdown-section-set-by-retailer-text'
                    ),
                    price: allowance,
                  },
                  {
                    label: contentManager.get(
                      'price-breakdown-section-subsidy-label'
                    ),
                    price: grants,
                  },
                  {
                    label: contentManager.get('price-breakdown-section-discount-label'),
                    price: discount,
                  },
                  {
                    label: contentManager.get('total-price-label'),
                    price: outstandingTotal,
                    bold: true,
                  },
                  ...(configuration?.pdbData?.fleetMode ? [
                    {
                      label: contentManager.get('p11d-price-label'),
                      price: configuration?.pdbData?.pricing?.p11dPrice,
                      bold: true,
                    },
                  ] : [])
                ],
                stackedDesktop: true,
              },
              cta: {
                text: contentManager.get('ctas-see-model'),
                url: fullUrl,
                onClick: () => {
                  performVWBasicButtonClick({
                    label: contentManager.get('ctas-see-model'),
                    sectionName: SectionNames.COMPARE_PAGE_VEHICLE_CARDS,
                    configuration,
                    retailers,
                    analyticsManager,
                    analyticsBuilder,
                  });
                  navigate(fullUrl);
                },
              },
            };
          }
        }
      })
      .filter((vehicle) => !!vehicle) as VehicleCardProps[];
  }, [configurations]);

  return (
    <>
      <FixedTopContainer reveal={reveal}>
        <StyledVehicleCardsNav>
          <PageContainer>
            <VehicleCardsContainer
              onSlideChange={handleSlideChange}
              activeSlideIndex={activeSlideIndex}
              resetSlides={resetSlides}
              vehicles={vehicles}
              collapsed
            />
          </PageContainer>
        </StyledVehicleCardsNav>
      </FixedTopContainer>
      <StyledVehicleCards ref={ref}>
        <PageContainer>
          <VehicleCardsContainer
            onSlideChange={setActiveSlideIndex}
            activeSlideIndex={activeSlideIndex}
            resetSlides={resetSlides}
            vehicles={vehicles}
          />
        </PageContainer>
      </StyledVehicleCards>
    </>
  );
};

export default VehicleCards;
